import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import useWebsocket from "react-use-websocket";

import { SearchEmpty, ModalDefault } from "components";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";

import { setScreenStatus } from "store/modules/screenControl/actions";
import { setTimeToCount } from "store/modules/user/actions";
import { useAppSelector } from "store/rootReducer";

import { onLoadList } from "features/ImportCertificate/constants";
import { listAttendantsMconf } from "features/RightTemplate/constants";

import { MenuListMconf } from "components/MenuListMconf";

import { MeetingMConfService } from "services/meetingMConf";

import V from "assets/svg/v.svg";

import { isProduct, ProductId } from "multitenancy/product";
import { FORMS_ADMIN, FORMS_HEARING } from "utils/entities/permission";
import { useQuery } from "utils/get";

import {
  BodyContainer,
  ButtonFinish,
  Container,
  ContainerDescription,
  HeaderContainer,
  Subtitle,
  Title,
} from "./styles";

export default function HearingRecordMConf() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { addToast } = useToast();
  const query = useQuery();

  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [loadingStopRecording, setLoadingStopRecording] = useState(false);
  const [hearingStatus, setHearingStatus] = useState<number | null>(null);
  const [listAttendant, setListAttendant] = useState<any[]>([]);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const hasShownTimerToast = useRef(false);
  const { create } = usePermissions(FORMS_HEARING.HEARING);

  const { timeToCount, user } = useAppSelector((s) => s.user);
  const { globalConfigurations } = useAppSelector((s) => s.configurations);

  const hearingId = query.get("hearingId");
  const hearingFileId = query.get("hearingFileId");
  const queryAttendantId = query.get("attendantId");
  const isInqueritoProduct = isProduct(ProductId.Inquerito);
  const { accessToken } = useAppSelector((s) => s.auth);
  const WS_URL_HEARING_STATUS = useMemo(
    () =>
      `${import.meta.env.VITE_APP_WSS_BASE_URL}/hearingFile?token=${accessToken}`,
    [accessToken],
  );

  const { create: createAttendant, exclude: excludeAttendant } = usePermissions(
    FORMS_ADMIN.ATTENDANT,
  );

  const recordProvider = globalConfigurations?.find(
    (c: any) => c.name === "RecordProvider",
  )?.value;
  const disabledCopyAttendantLink = recordProvider !== "2";

  const [socketData, setSocketData] = useState<any>(null);

  const { sendMessage, getWebSocket } = useWebsocket(WS_URL_HEARING_STATUS, {
    onMessage: (event: MessageEvent<any>) => {
      try {
        const data = JSON.parse(event.data);

        setSocketData(data);

        if (
          data?.OK === true &&
          data?.Data?.HearingInProgress &&
          Array.isArray(data.Data.HearingInProgress)
        ) {
          const currentHearingFile = data.Data.HearingInProgress.find(
            (item: any) => item.Id === Number(hearingFileId),
          );
          if (currentHearingFile) {
            setHearingStatus(currentHearingFile.Status);
          }
        }
      } catch (error) {
        console.error("Erro ao processar mensagem do WebSocket:", error);
      }
    },
    onOpen: () => {
      if (hearingId) {
        sendMessage(hearingId.toString());
      }
    },

    onClose: () => {},

    shouldReconnect: () => true,
    reconnectInterval: 1000,
  });

  const disabled = false;
  const audience = { status: "A" };

  const onClickDetach = () => {};

  const onClickCopyLinkAttendant = async (item: any) => {
    await navigator.clipboard.writeText(item.link);
    addToast({
      type: "success",
      title: t("recordings.messages.linkCopied"),
      description: t("recordings.messages.linkCopiedDesc"),
    });
  };
  const getTimeOfRecordForUser = useCallback(
    async (customerId: number) => {
      const response = await onLoadList(customerId);
      if (response.data && response.data.certificateType === 2) {
        dispatch(setTimeToCount(response.data.limitRecord));
      }
    },
    [dispatch],
  );

  const clearTimers = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    hasShownTimerToast.current = false;
  };

  const handleConfirmEndMeeting = useCallback(
    async (isAuto = false) => {
      clearTimers();

      if (loadingStopRecording) return;

      const hearingFileId = Number(query.get("hearingFileId"));
      setLoadingStopRecording(true);

      const resp = await MeetingMConfService.endMeeting(hearingFileId);

      if (resp.status) {
        addToast({
          type: "success",
          title: isAuto
            ? t("recordings.messages.timeExpired")
            : t("recordings.messages.recordingEnded"),
          description: isAuto
            ? t("recordings.messages.autoEndedDesc")
            : t("recordings.messages.endedSuccessDesc"),
        });

        const hearingId = query.get("hearingId");
        const hearingFileIdQS = query.get("hearingFileId");
        navigate(
          `/audiences?hearingId=${hearingId}&hearingFileId=${hearingFileIdQS}`,
          {
            state: { procedureNumber: location.state?.procedureNumber },
          },
        );
      } else {
        addToast({
          type: "error",
          title: t("recordings.messages.endError"),
          description: resp.message ?? t("recordings.messages.endErrorDesc"),
        });
      }

      setLoadingStopRecording(false);
      setShowModalConfirmation(false);
    },
    [
      loadingStopRecording,
      query,
      addToast,
      navigate,
      location.state?.procedureNumber,
    ],
  );

  const armLimitTimers = useCallback(() => {
    if (hearingStatus !== 12) return;

    if (timeoutsRef.current.length > 0) return;

    const showTimerRecord = globalConfigurations?.find(
      (c: { name: string }) => c.name === "ShowTimerRecord",
    );
    const shouldShowToast = showTimerRecord?.visible === true;

    const minutes = Number(timeToCount);

    if (!shouldShowToast || !minutes || minutes <= 0) return;

    if (!hasShownTimerToast.current) {
      addToast({
        type: "warning",
        title: t("common.attention"),
        description: t("recordings.messages.timeLimitWarning", { minutes }),
        timeLimitToast: 15000,
      });
      hasShownTimerToast.current = true;
    }

    const ms = minutes * 60 * 1000;

    const finishT = setTimeout(async () => {
      try {
        await handleConfirmEndMeeting(true);
      } catch {
        /**/
      }
    }, ms);

    let warnT: ReturnType<typeof setTimeout> | null = null;

    if (ms >= 30000) {
      warnT = setTimeout(() => {
        addToast({
          type: "warning",
          title: t("common.warning"),
          description: t("recordings.messages.thirtySecondsWarning"),
          timeLimitToast: 15000,
        });
      }, ms - 30000);
    }

    timeoutsRef.current = warnT ? [finishT, warnT] : [finishT];
  }, [
    hearingStatus,
    globalConfigurations,
    timeToCount,
    addToast,
    handleConfirmEndMeeting,
  ]);

  useEffect(() => {
    if (!location.state) {
      navigate("/audiencias");
      return;
    }
    dispatch(setScreenStatus(true));

    return () => {
      dispatch(setScreenStatus(false));
    };
  }, [location.state, navigate, dispatch]);

  useEffect(() => {
    if (!hearingId) return;

    const interval = setInterval(() => {
      sendMessage(hearingId.toString());
    }, 4000);

    return () => clearInterval(interval);
  }, [hearingId, sendMessage]);

  useEffect(() => {
    if (hearingStatus === 12 && create && location.state?.mconfJoinUrl) {
      if (timeoutsRef.current.length === 0) {
        armLimitTimers();
      }
    }
  }, [hearingStatus, create, location.state?.mconfJoinUrl, armLimitTimers]);

  useEffect(() => {
    if (user?.customerId) {
      getTimeOfRecordForUser(user.customerId);
    }
  }, [user?.customerId, getTimeOfRecordForUser]);

  useEffect(() => {
    return () => {
      const webSocket = getWebSocket();
      if (webSocket) {
        webSocket.close();
      }
    };
  }, [getWebSocket]);

  useEffect(() => {
    if (socketData?.Data?.HearingAttendants) {
      const adapted = socketData.Data.HearingAttendants.map((att: any) => ({
        id: att.AttendantId,
        name: att.Name,
        link: att.Link,
        role: att.Role,
      }));
      const filteredAttendants =
        isInqueritoProduct && queryAttendantId
          ? adapted.filter(
              (att: any) => Number(att.id) === Number(queryAttendantId),
            )
          : adapted;

      setListAttendant(filteredAttendants);
    }
  }, [isInqueritoProduct, queryAttendantId, socketData]);

  return (
    <Container>
      <HeaderContainer>
        <ContainerDescription>
          <Title style={{ paddingLeft: 20 }}>
            {t("audiences.singular")} {location.state?.procedureNumber}
          </Title>
          <Subtitle>{location.state?.procedureCustomerSourceName}</Subtitle>
        </ContainerDescription>
        <MenuListMconf
          disabled={disabled}
          title={t("recordings.shareLink")}
          list={
            listAttendantsMconf(
              listAttendant,
              audience,
              onClickDetach,
              excludeAttendant,
              onClickCopyLinkAttendant,
              disabledCopyAttendantLink,
            ) || []
          }
          handleOpen={() => t("participants.title")}
          emptyMessage={t("participants.notFound")}
        />

        <ButtonFinish
          id="finish-record"
          color="forth"
          onClick={() => setShowModalConfirmation(true)}
          disabled={hearingStatus !== 12}
          title={
            hearingStatus !== 12
              ? t("recordings.waitingStatus")
              : t("recordings.end")
          }
        >
          <img src={V} alt="" style={{ marginRight: 8, height: 20 }} />
          {t("recordings.end")}
        </ButtonFinish>
      </HeaderContainer>

      {create ? (
        <BodyContainer>
          <iframe
            id="mconfIframe"
            style={{ width: "100%", height: "100%", border: "none" }}
            src={location.state?.mconfJoinUrl}
            title="Provedor de gravação Join"
            allowFullScreen
            allow="microphone *; camera *; display-capture *; clipboard-read *; clipboard-write *; screen-wake-lock *;"
            data-hj-allow-iframe=""
          />
        </BodyContainer>
      ) : (
        <div style={{ height: "100%", marginTop: 30 }}>
          <BodyContainer>
            <SearchEmpty
              typeIcon="avatar"
              textMessage={t("common.noPermissionForProfile")}
            />
          </BodyContainer>
        </div>
      )}

      <ModalDefault
        openModal={showModalConfirmation}
        setOpenModal={setShowModalConfirmation}
        title={t("recordings.end")}
        bodyMessageTitle={t("recordings.endConfirmTitle")}
        bodyMessageSubTitle={t("recordings.endConfirmSubtitle")}
        textConfirm={t("recordings.endButton")}
        onClickConfirm={() => handleConfirmEndMeeting(false)}
        textCancel={t("common.cancel").toUpperCase()}
        bodyMessageMarginBetweenTitleAndSubTitle="30px"
        bodyMessageMarginTop="30px"
        onClickCancel={() => setShowModalConfirmation(false)}
      />
    </Container>
  );
}
